import type { NostrEvent } from '@nostrify/nostrify';

/** Get a deterministic integer from a hex string slice. Safe against out-of-bounds. */
export function seedFromHex(hex: string, start: number, end: number): number {
  // Wrap indices to stay within string bounds
  const len = hex.length || 1;
  const s = start % len;
  const e = Math.min(s + (end - start), len);
  const result = parseInt(hex.slice(s, e), 16);
  return isNaN(result) ? 0 : result;
}

/** Get card rotation in degrees (-4 to +4), deterministic from event ID. */
export function getCardRotation(id: string): number {
  return (seedFromHex(id, 0, 4) % 9) - 4;
}

/** Get grid card rotation in degrees (-5 to +5), deterministic from event ID. */
export function getGridRotation(id: string): number {
  return (seedFromHex(id, 0, 4) % 11) - 5;
}

/** Get tape variant index (0-7), deterministic from event ID.
 *  Uses XOR of multiple spread-out bytes for better distribution
 *  and to avoid correlation with card rotation (which uses chars 0-4). */
export function getTapeVariant(id: string): number {
  // XOR three bytes from different parts of the hex ID for good mixing
  const a = seedFromHex(id, 8, 10);
  const b = seedFromHex(id, 24, 26);
  const c = seedFromHex(id, 48, 50);
  return (a ^ b ^ c) % 8;
}

/** Get tape rotation in degrees (-5 to +5), deterministic from event ID. */
export function getTapeRotation(id: string): number {
  return (seedFromHex(id, 4, 8) % 11) - 5;
}

/** Whether to use a pushpin instead of tape (for grid), deterministic. */
export function shouldUsePushpin(id: string): boolean {
  return seedFromHex(id, 2, 4) % 3 === 1;
}

/** Get pushpin color, deterministic. */
export function getPushpinColor(id: string): string {
  const colors = ['#E8D44D', '#6DB86B', '#E88BA0'];
  return colors[seedFromHex(id, 4, 6) % 3];
}

/** Get doodle type for a given position.
 *  Color uses 'currentColor' so doodles inherit the text color from the theme. */
export function getDoodleType(id: string, position: number): { type: 'heart' | 'star' | 'sparkle' | 'diamond' | 'flower' | 'butterfly'; color: string } {
  const types = ['heart', 'star', 'sparkle', 'diamond', 'flower', 'butterfly'] as const;
  // Keep slice indices within the 64-char hex ID bounds
  const start = (position * 2) % 60;
  const seed = seedFromHex(id, start, start + 4);
  return {
    type: types[seed % types.length],
    color: 'currentColor',
  };
}

/** Get squiggle type for hand-drawn line decorations.
 *  Color uses 'currentColor' so squiggles inherit the text color from the theme. */
export function getSquiggleType(id: string, position: number): { type: 'swirlyArrow' | 'wavyLine' | 'spiral' | 'loopyUnderline' | 'xmark'; color: string } {
  const types = ['swirlyArrow', 'wavyLine', 'spiral', 'loopyUnderline', 'xmark'] as const;
  // Keep slice indices within the 64-char hex ID bounds
  const start = ((position * 3) + 4) % 60;
  const seed = seedFromHex(id, start, start + 4);
  return {
    type: types[seed % types.length],
    color: 'currentColor',
  };
}

/** Whether this card should have doodles (more frequent — roughly every other card). */
export function shouldHaveDoodles(id: string, index: number): boolean {
  return (index + seedFromHex(id, 6, 8)) % 2 === 0;
}

/** Whether this feed gap should have a squiggle (every 3rd-4th gap). */
export function shouldHaveSquiggle(id: string, index: number): boolean {
  return (index + seedFromHex(id, 8, 10)) % 3 === 1;
}

/** Whether this card has a description worth showing as a background note. */
export function hasBackgroundNote(event: NostrEvent): boolean {
  const title = getTitle(event);
  // Show note when there's real content AND a title (content is extra, not the caption)
  if (title && event.content && event.content.length > 10) return true;
  // Also show if content is long enough to be interesting even without title
  if (!title && event.content && event.content.length > 30) return true;
  return false;
}

/** Get the content-warning reason from an event (NIP-36). Returns the reason string, or true if tagged without reason, or null if no CW. */
export function getContentWarning(event: NostrEvent): string | true | null {
  const cwTag = event.tags.find(([name]) => name === 'content-warning');
  if (!cwTag) return null;
  const reason = cwTag[1]?.trim();
  return reason || true;
}

/** Get hashtags from a kind 20 event's t tags. */
export function getHashtags(event: NostrEvent): string[] {
  return event.tags
    .filter(([name]) => name === 't')
    .map(([, value]) => value)
    .filter((v): v is string => !!v);
}

/** Get a deterministic pastel color for a hashtag string — fully opaque. */
export function getHashtagColor(tag: string): { bg: string; text: string } {
  const colors = [
    { bg: '#F8D8E8', text: '#A05070' },
    { bg: '#E0D0F0', text: '#7050A0' },
    { bg: '#D0E8F8', text: '#406888' },
    { bg: '#D0F0E8', text: '#308060' },
    { bg: '#F0E8D0', text: '#907830' },
    { bg: '#F8D0E0', text: '#A06080' },
  ];
  let hash = 0;
  for (let i = 0; i < tag.length; i++) {
    hash = ((hash << 5) - hash) + tag.charCodeAt(i);
    hash |= 0;
  }
  return colors[Math.abs(hash) % colors.length];
}

/** Get an offset for scatter layout positioning. */
export function getScatterOffset(id: string): { x: number; y: number } {
  const x = (seedFromHex(id, 8, 12) % 17) - 8;
  const y = (seedFromHex(id, 12, 16) % 17) - 8;
  return { x, y };
}

/** Format a unix timestamp as a polaroid-style date: "24 AUG '25" */
export function formatPolaroidDate(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = String(date.getFullYear()).slice(-2);
  return `${day} ${month} '${year}`;
}

/** Extract the first image URL from a kind 20 event's imeta tags. */
export function getImageUrl(event: NostrEvent): string | undefined {
  const imetaTag = event.tags.find(([name]) => name === 'imeta');
  if (!imetaTag) return undefined;
  const urlEntry = imetaTag.find(entry => entry.startsWith('url '));
  return urlEntry?.slice(4);
}

/** Extract all image URLs from a kind 20 event's imeta tags. */
export function getAllImageUrls(event: NostrEvent): string[] {
  return event.tags
    .filter(([name]) => name === 'imeta')
    .map(tag => {
      const urlEntry = tag.find(entry => entry.startsWith('url '));
      return urlEntry?.slice(4);
    })
    .filter((url): url is string => !!url);
}

/** Extract alt text from an imeta tag. */
export function getImageAlt(event: NostrEvent): string {
  const imetaTag = event.tags.find(([name]) => name === 'imeta');
  if (!imetaTag) return 'Photo';
  const altEntry = imetaTag.find(entry => entry.startsWith('alt '));
  return altEntry?.slice(4) ?? 'Photo';
}

/** Get the title from a kind 20 event. */
export function getTitle(event: NostrEvent): string | undefined {
  return event.tags.find(([name]) => name === 'title')?.[1];
}

/** Replace nostr:npub1.../nprofile1... references with a short @npub1abc… form */
function shortenMentions(text: string): string {
  return text.replace(
    /nostr:(npub1[023456789acdefghjklmnpqrstuvwxyz]{6,}|nprofile1[023456789acdefghjklmnpqrstuvwxyz]{6,})/g,
    (_match, bech32: string) => `@${bech32.slice(0, 10)}…`,
  );
}

/** Get display caption: title if available, or truncated content. Shortens npub mentions. */
export function getCaption(event: NostrEvent): string {
  const title = getTitle(event);
  if (title) return shortenMentions(title);
  if (event.content) {
    const cleaned = shortenMentions(event.content);
    return cleaned.length > 60 ? cleaned.slice(0, 60) + '...' : cleaned;
  }
  return '';
}

/**
 * Default pastel colors used as decorative "construction paper" strip
 * backgrounds when no Ditto theme is active (default scrapbook look).
 */
export const PASTEL_STRIP_COLORS = ['#fbc7d3', '#bbe0bf', '#cbf1ed', '#dac5e6', '#f1bf60'];

/** Pick a deterministic pastel strip color from a seed string.
 *  Uses a full string hash so non-hex suffixes (e.g. 'date', 'tag') affect the result. */
export function getPastelStripColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash) + seed.charCodeAt(i);
    hash |= 0;
  }
  return PASTEL_STRIP_COLORS[Math.abs(hash) % PASTEL_STRIP_COLORS.length];
}

/** Format relative time: "2h ago", "3d ago", etc. */
export function formatRelativeTime(timestamp: number): string {
  const now = Math.floor(Date.now() / 1000);
  const diff = now - timestamp;

  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  if (diff < 2592000) return `${Math.floor(diff / 604800)}w ago`;
  return formatPolaroidDate(timestamp);
}
